import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";

const AuthContext = createContext(null);

/** Any local evidence of a prior session. Auth is cookie-first on the server
 *  (see core/deps.get_current_user) and the cookie is httpOnly, so JS cannot
 *  read it — these localStorage keys are the only client-side hint that a
 *  session may exist. Either key alone is enough to warrant verifying with
 *  /auth/me: a genuinely logged-out visitor has neither and skips the call. */
function hasSessionHint() {
  try {
    return !!(localStorage.getItem("token") || localStorage.getItem("user"));
  } catch {
    return false;
  }
}

function readCachedUser() {
  try {
    const stored = localStorage.getItem("user");
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

export function AuthProvider({ children }) {
  // Seed user from localStorage immediately so protected layouts don't remount
  // after the auth check completes. loading=true only when a session may exist
  // and must be validated — ProtectedRoute blocks render until that resolves.
  const [user, setUser] = useState(readCachedUser);
  const [loading, setLoading] = useState(hasSessionHint);

  const checkAuth = useCallback(async () => {
    if (!hasSessionHint()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
    }
  }, []);

  // Guard the mount-time verification only. checkAuth itself stays freely
  // callable — PubProfile re-runs it after a profile save to refresh `user`.
  const didVerify = useRef(false);
  useEffect(() => {
    if (didVerify.current) return;
    didVerify.current = true;
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data));
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await API.post("/auth/register", payload);
    // New registrations land as `pending_approval` — admin must approve
    // before they can log in. Don't auto-login; let the caller handle the
    // "check your inbox" UX.
    if (data.status === "pending_approval") {
      return data;
    }
    if (data.token) localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
